/**
 * The wire contract between the Worker and the SPA.
 * Both sides import from here; nothing else is shared.
 */

// ── Identity ────────────────────────────────────────────────────────────────

export type NameState = 'held' | 'claimed' | 'pending'

export interface User {
  id: string
  displayName: string
  isVerified: boolean
  isAdmin: boolean
  nameState: NameState
  teamId: string
  secondaryTeamId: string | null
  notifPrefs: NotifPrefs
  quietStartMin: number
  quietEndMin: number
  theme: 'system' | 'dark' | 'light'
  textSize: 'normal' | 'large'
  createdAt: number
  /**
   * Their own address, masked — `r•••@gmail.com`. Their own, in their own
   * settings, so there is nothing to protect it from; masked anyway because a
   * screen somebody might hand over at a party does not need to print it in
   * full. Null when no address is attached.
   *
   * Settings used to read this out of `sessionStorage`, written when the
   * verify email was sent. That works exactly once, on the device that asked —
   * so signing in from the emailed link on a phone left the settings screen
   * claiming the account was unconfirmed on a session that had just proved the
   * opposite. `isVerified` and this field now come from the same row.
   */
  emailMasked: string | null
  /**
   * They've stepped out of the social half of the app: Shouts, Online, Picks
   * and Feats are off for them, and they're absent from the lists those
   * features draw. Reversible at any time from Settings.
   */
  optedOut: boolean
}

export interface PublicUser {
  id: string
  displayName: string
  isVerified: boolean
  teamId: string
  initials: string
}

export interface NotifPrefs {
  challenge_resolved: boolean
  challenge_proposed: boolean
  team_final: boolean
  food_arriving: boolean
  attendance_poll: boolean
  fund_reminder: boolean
  game_days_only: boolean
  feature_me_in_stats: boolean
}

export const DEFAULT_NOTIF_PREFS: NotifPrefs = {
  challenge_resolved: true,
  challenge_proposed: true,
  team_final: true,
  food_arriving: true,
  attendance_poll: true,
  fund_reminder: false,
  game_days_only: false,
  feature_me_in_stats: true,
}

// ── NFL ─────────────────────────────────────────────────────────────────────

export type GameState = 'pre' | 'in' | 'post'

export interface GameSituation {
  possessionTeamId: string | null
  downDistanceText: string | null
  possessionText: string | null
  isRedZone: boolean
  lastPlayText: string | null
}

export interface Game {
  id: string
  weekId: string
  kickoffAt: number
  homeTeamId: string
  awayTeamId: string
  homeScore: number | null
  awayScore: number | null
  state: GameState
  statusName: string | null
  statusDetail: string | null
  period: number | null
  clockDisplay: string | null
  linescores: { home: number[]; away: number[] } | null
  spread: number | null
  overUnder: number | null
  homeWinProb: number | null
  wentToOt: boolean
  situation: GameSituation | null
}

export interface ScoringPlay {
  id: string
  gameId: string
  sequence: number
  teamId: string
  period: number
  clockDisplay: string
  text: string
  scoreValue: number
  homeScore: number
  awayScore: number
}

export interface WeekRef {
  id: string
  seasonYear: number
  seasonType: number
  weekNumber: number
  label: string
  startsAt: number
  endsAt: number
  gatheringDate: string | null
  hostUserId: string | null
  /** An admin can switch a week off; resolution then lands on the next one. */
  isEnabled: boolean
}

/** One entry in a week picker — enough to label a button, and nothing else. */
export interface WeekOption {
  id: string
  label: string
  startsAt: number
  endsAt: number
  isEnabled: boolean
  isCurrent: boolean
  isPast: boolean
}

export interface Standing {
  teamId: string
  wins: number
  losses: number
  ties: number
  winPct: number
  playoffSeed: number | null
  conference: string
  division: string
}

// ── Attendance ──────────────────────────────────────────────────────────────

export type AttendanceStatus = 'in' | 'out'

export interface AttendanceRow {
  userId: string
  displayName: string
  status: AttendanceStatus
  guests: number
  changedAfterCutoff: boolean
}

export interface AttendanceSummary {
  weekId: string
  mine: AttendanceStatus | null
  myGuests: number
  in: number
  out: number
  unknown: number
  headcount: number
  cutoffPassed: boolean
  roster: AttendanceRow[]
}

export interface SeasonAttendanceWeek {
  week: WeekRef
  mine: AttendanceStatus | null
  myGuests: number
  in: number
  out: number
  unknown: number
  hostName: string | null
  editable: boolean
  isCurrent: boolean
  attended: boolean
}

// ── Food ────────────────────────────────────────────────────────────────────

export type FoodCategory =
  | 'pizza' | 'wings' | 'burgers' | 'tacos'
  | 'sushi' | 'dessert' | 'drinks' | 'other'

export const FOOD_CATEGORIES: { key: FoodCategory; label: string; emoji: string }[] = [
  { key: 'pizza', label: 'Pizza', emoji: '🍕' },
  { key: 'wings', label: 'Wings', emoji: '🍗' },
  { key: 'burgers', label: 'Burgers', emoji: '🍔' },
  { key: 'tacos', label: 'Tacos', emoji: '🌮' },
  { key: 'sushi', label: 'Sushi', emoji: '🍣' },
  { key: 'dessert', label: 'Dessert', emoji: '🍩' },
  { key: 'drinks', label: 'Drinks', emoji: '🥤' },
  { key: 'other', label: 'Other', emoji: '🍽️' },
]

export interface FoodAddon {
  id: string
  userId: string
  displayName: string
  request: string
  createdAt: number
}

export interface FoodOrder {
  id: string
  gameDay: string
  userId: string
  displayName: string
  category: FoodCategory
  vendor: string | null
  contents: string
  quantity: number | null
  quantityEstimated: boolean
  notes: string | null
  etaAt: number | null
  placedAt: number
  deliveredAt: number | null
  deliveredByName: string | null
  cancelledAt: number | null
  editedAt: number | null
  groupedWith: string | null
  addons: FoodAddon[]
}

export interface FoodIntent {
  id: string
  gameDay: string
  userId: string
  displayName: string
  category: FoodCategory
  approxQty: string | null
  vendor: string | null
  createdAt: number
  expiresAt: number
}

export interface FoodConflict {
  type: 'intent' | 'order'
  id: string
  userId: string
  displayName: string
  category: FoodCategory
  approxQty: string | null
  vendor: string | null
  minutesAgo: number
}

export interface FoodSummary {
  gameDay: string
  headcount: number
  servings: number
  sufficiencyLow: number
  sufficiencyHigh: number
  sufficiencyLabel: string
  mealLight: boolean
  orders: FoodOrder[]
  intents: FoodIntent[]
}

export interface VendorSuggestion {
  vendor: string
  vendorKey: string
  uses: number
  lastUsed: number
  typicalEtaMin: number | null
  typicalCategory: FoodCategory | null
}

// ── Food Fund ───────────────────────────────────────────────────────────────

export type ContributionStatus = 'claimed' | 'confirmed' | 'disputed' | 'voided'
export type PayMethod = 'venmo' | 'cashapp' | 'paypal' | 'zelle' | 'cash' | 'other'

export interface Contribution {
  id: string
  userId: string
  displayName: string
  amountC: number
  method: PayMethod
  status: ContributionStatus
  claimedAt: number
  confirmedAt: number | null
}

export interface Receipt {
  id: string
  userId: string
  displayName: string
  vendor: string
  amountC: number
  photoKey: string | null
  note: string | null
  createdAt: number
}

export interface FundSummary {
  weekId: string
  weekLabel: string
  gatheringDate: string | null
  hostUserId: string | null
  hostName: string | null
  targetC: number
  carryoverInC: number
  confirmedC: number
  pendingC: number
  spentC: number
  neededC: number
  varianceC: number
  suggestedC: number
  contributorCount: number
  canConfirm: boolean
  iContributed: boolean
  destinations: { method: PayMethod; handle: string; confirmed: boolean }[]
  contributions: Contribution[]
  receipts: Receipt[]
}

export interface PayLink {
  method: PayMethod
  deepLink: string | null
  appLink: string | null
  handle: string
  amountDisplay: string
  qrPayload: string | null
  note: string
}

// ── Challenges ──────────────────────────────────────────────────────────────

export type ChallengeStatus =
  | 'proposed' | 'accepted' | 'declined' | 'expired'
  | 'resolved' | 'disputed' | 'settled' | 'voided'

export type ChallengeOutcome = 'side_a' | 'side_b' | 'push' | 'void'
export type ResolutionSource = 'auto' | 'participant' | 'admin'
export type StakeMode = 'per_person' | 'split'
export type Side = 'a' | 'b'

export interface ChallengeParticipant {
  id: string
  side: Side
  userId: string | null
  displayLabel: string | null
  displayName: string
  acceptedAt: number | null
  declinedAt: number | null
  settledAt: number | null
  settledSelf: boolean
  settledByName: string | null
  isLinked: boolean
  isFreeform: boolean
}

export interface Challenge {
  id: string
  creatorId: string
  creatorName: string
  gameId: string | null
  gameLabel: string | null
  seasonYear: number | null
  templateId: string
  params: Record<string, string | number>
  proposition: string
  oppositionText: string | null
  stakeQuantity: number
  stakeUnit: string
  stakeMode: StakeMode
  stakeLabel: string
  isPublic: boolean
  status: ChallengeStatus
  outcome: ChallengeOutcome | null
  resolutionSrc: ResolutionSource | null
  resolvedByName: string | null
  resolvedAt: number | null
  confirmDueAt: number | null
  confirmedAt: number | null
  disputeNote: string | null
  locksAt: number | null
  isLocked: boolean
  adminOverride: boolean
  createdAt: number
  sides: { a: ChallengeParticipant[]; b: ChallengeParticipant[] }
  viewer: {
    isParticipant: boolean
    side: Side | null
    needsAccept: boolean
    needsConfirm: boolean
    canResolve: boolean
    canSettle: boolean
    won: boolean | null
  }
}

export interface ChallengeLists {
  needsYou: Challenge[]
  live: Challenge[]
  open: Challenge[]
  resolved: Challenge[]
  counts: { needsYou: number; live: number; open: number; resolved: number }
}

export type TemplateScope = 'game' | 'season' | 'either'
export type TemplateParamType =
  | 'team_in_game' | 'any_team' | 'enum' | 'int' | 'half_point' | 'string'

export interface TemplateParam {
  key: string
  type: TemplateParamType
  values?: (string | number)[]
  min?: number
  max?: number
  required?: boolean
}

export interface ChallengeTemplate {
  id: string
  label: string
  phrasing: string
  oppositionPhrasing?: string
  params: TemplateParam[]
  scope: TemplateScope
  locksAtRule: string
  autoResolve: boolean
  tieRule: 'push' | 'void' | 'none' | 'impossible' | 'manual'
  /** populated per-game by the API */
  valid?: boolean
  invalidReason?: string
}

export interface StakeUnit {
  key: string
  label: string
  isSeeded: boolean
  useCount: number
}

// ── Pick'em ─────────────────────────────────────────────────────────────────

export interface PickGame {
  game: Game
  myPick: string | null
  locked: boolean
  correct: boolean | null
  consensus: { teamId: string; pct: number }[] | null
}

export interface PicksWeek {
  weekId: string
  weekLabel: string
  made: number
  total: number
  games: PickGame[]
  tiebreakerGameId: string | null
  tiebreakerValue: number | null
}

/** One row of the "who has picked" board on /picks. */
export interface PicksBoardEntry {
  userId: string
  displayName: string
  initials: string
  teamId: string
  made: number
  points: number
  isViewer: boolean
}

export interface PicksBoard {
  weekId: string
  weekLabel: string
  weeks: WeekOption[]
  total: number
  entries: PicksBoardEntry[]
}

/**
 * One game as it looks when you hold somebody else's card next to your own.
 *
 * `theirPick` is null and `hidden` is true until that game kicks off, which is
 * D84 — individual picks stay private until they can no longer be copied.
 */
export interface PicksCompareRow {
  game: Game
  theirPick: string | null
  myPick: string | null
  hidden: boolean
  locked: boolean
  theirCorrect: boolean | null
  myCorrect: boolean | null
}

export interface PicksCompare {
  weekId: string
  weekLabel: string
  weeks: WeekOption[]
  user: { id: string; displayName: string; initials: string; teamId: string }
  made: number
  total: number
  points: number
  rows: PicksCompareRow[]
}

export interface StandingRow {
  userId: string
  displayName: string
  rank: number
  points: number
  picksMade: number
  perPick: number
  joinedWeek: string | null
  isViewer: boolean
}

// ── Home / state ────────────────────────────────────────────────────────────

export interface RoomFavorite {
  gameId: string
  label: string
  reason: string
  score: number
}

export interface RivalryCard {
  opponentTeamId: string
  tier: 'historic' | 'divisional' | 'regional'
  title: string
  allTime: string
  since1999: string
  lastFive: string[]
  blurb: string
  odds: { spread: string; overUnder: number; winProbPct: number | null } | null
}

export interface FeedEvent {
  id: string
  at: number
  kind: 'score' | 'challenge' | 'food' | 'fund' | 'attendance'
  emoji: string | null
  title: string
  detail: string | null
  highlight: boolean
  href: string | null
}

// ── Shouts ──────────────────────────────────────────────────────────────────

export interface Shout {
  id: string
  weekId: string
  userId: string
  displayName: string
  initials: string
  teamId: string
  body: string
  /** What the feed renders: the small copy when there is one. Never an R2 key. */
  imageUrl: string | null
  /** Set only when a smaller display copy exists, i.e. when a download is worth offering. */
  fullImageUrl: string | null
  /** Size of the original, for the download label. */
  imageBytes: number | null
  mentions: { userId: string; displayName: string }[]
  createdAt: number
  isMine: boolean
}

export interface ShoutsPage {
  weekId: string
  weekLabel: string
  weeks: WeekOption[]
  shouts: Shout[]
}

// ── Timeline ────────────────────────────────────────────────────────────────

export type TimelineKind =
  | 'score' | 'final' | 'shout' | 'feat' | 'attendance' | 'fund' | 'picks' | 'medal'

export interface TimelineEvent {
  id: string
  at: number
  kind: TimelineKind
  emoji: string | null
  title: string
  detail: string | null
  href: string | null
  teamId: string | null
  /** Set when this event is about the viewer's own team, for the accent colour. */
  highlight: boolean
  /**
   * The picture that came with a shout, when there was one — already the small
   * display copy, never the original and never an R2 key. Only `kind: 'shout'`
   * ever sets it; everything else leaves it null.
   */
  imageUrl?: string | null
  /**
   * Where the viewer goes to correct this event, when it is theirs to correct.
   * The server decides — a row only carries this for the person who made it —
   * so the client can render the control without knowing the ownership rules
   * of every kind of event. Today only chip-ins set it.
   */
  editHref?: string | null
}

export interface TimelinePage {
  weekId: string
  weekLabel: string
  weeks: WeekOption[]
  events: TimelineEvent[]
}

// ── Who's rooting for whom ──────────────────────────────────────────────────

export interface RootingFan {
  id: string
  displayName: string
  initials: string
}

export interface RootingTeam {
  teamId: string
  /** "3-1", or "3-1-1" when there is a tie in it. Computed from finished games. */
  record: string
  fans: RootingFan[]
}

export interface RootingPage {
  seasonYear: number
  /** Everyone seen since this moment is counted — two weeks before the newest live week. */
  since: number
  teams: RootingTeam[]
}

// ── Who's around ────────────────────────────────────────────────────────────

export interface OnlineUser {
  id: string
  displayName: string
  initials: string
  teamId: string
  isVerified: boolean
  lastSeenAt: number
  isViewer: boolean
}

export interface AppState {
  user: User
  now: number
  serverTz: string
  gameDay: { active: boolean; date: string }
  week: WeekRef | null
  attendance: AttendanceSummary | null
  games: Game[]
  myGame: Game | null
  scoresAsOf: number | null
  feedHealth: 'ok' | 'stale' | 'paused' | 'unavailable'
  food: FoodSummary | null
  fund: FundSummary | null
  challenges: { needsYou: Challenge[]; live: Challenge[]; counts: { needsYou: number; open: number; resolved: number } }
  picks: {
    made: number
    total: number
    consensus: { gameId: string; pcts: { teamId: string; pct: number }[] }[]
    /**
     * Up to three games still open and unpicked, so Home can take the pick
     * itself instead of sending someone to another screen to tap twice.
     */
    outstanding: PickGame[]
  }
  roomFavorites: RoomFavorite[]
  rivalry: RivalryCard | null
  feed: FeedEvent[]
  /** The seven most recent things that happened this week (D121). */
  timeline: TimelineEvent[]
  roster: { present: PublicUser[]; overflow: number; fansOfMyTeam: number }
  bestCall: { displayName: string; detail: string } | null
  roughDay: { displayName: string; detail: string } | null
}

// ── Realtime ────────────────────────────────────────────────────────────────

export type ServerMessage =
  | { t: 'hello'; date: string; serverTime: number }
  | { t: 'score'; game: Game }
  | { t: 'scoring'; gameId: string; play: ScoringPlay }
  | { t: 'challenge'; op: 'created' | 'accepted' | 'resolved' | 'settled' | 'voided'; id: string }
  | { t: 'food'; op: 'created' | 'updated' | 'delivered' | 'intent' | 'addon'; gameDay: string }
  | { t: 'fund'; weekId: string; confirmedC: number; pendingC: number }
  | { t: 'attend'; weekId: string; in: number; out: number; unknown: number }
  | { t: 'shout'; weekId: string }
  | { t: 'stale'; since: number; health: 'ok' | 'stale' | 'paused' }
  | { t: 'rollover'; date: string }

export type ClientMessage =
  | { t: 'ping' }
  | { t: 'hello'; date: string }

// ── Errors ──────────────────────────────────────────────────────────────────

export type ApiErrorCode =
  | 'unauthenticated' | 'forbidden' | 'not_found'
  | 'name_taken' | 'name_held' | 'name_pending'
  | 'locked' | 'already_resolved' | 'real_currency'
  | 'validation' | 'rate_limited' | 'upstream_unavailable'
  | 'destination_unconfirmed' | 'conflict' | 'server_error'

export interface ApiError {
  error: { code: ApiErrorCode; message: string; field?: string; retryAfter?: number }
}
